import styled from 'styled-components';

import { Avatar } from '@hivebrite/design-system/Avatar';
import { Box } from '@hivebrite/design-system/Box';
import { Card } from '@hivebrite/design-system/Card';
import { Dotdotdot } from '@hivebrite/design-system/Dotdotdot';
import { FakeLink } from '@hivebrite/design-system/Link';
import { ScreenReadersContent } from '@hivebrite/design-system/ScreenReadersContent';
import { scaleWithFont } from '@hivebrite/design-system/Theme';
import { Tooltip, POSITIONS } from '@hivebrite/design-system/Tooltip';

import { htmlToPlainText } from 'hb-react/frontoffice/apps/LiveFeed/utils/htmlToPlainText';
import { Link } from 'hb-react/frontoffice/components/Link';
import UserSnapshot from 'hb-react/shared/components/UserSnapshot';
import IconHeart from 'hb-react/shared/icons/Heart';
import IconPin from 'hb-react/shared/icons/Pin';
import onEnterOrSpacePress from 'hb-react/shared/utils/onEnterOrSpacePress';

const avatarFallbackImage = '/images/missing/user_avatar/large.jpg';

interface PostCardProps {
  avatarUrl?: string;
  canEdit?: boolean;
  content: string;
  href: string;
  id: number;
  likes: { count: number };
  onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  onKeyDown?: (event: React.KeyboardEvent) => any;
  openModal: () => any;
  totalComments: number;
  userId: number;
}

const PostCard = ({
  canEdit,
  totalComments = 0,
  content,
  href,
  id,
  likes,
  avatarUrl,
  onClick,
  onKeyDown,
  openModal,
  userId,
}: PostCardProps) => {
  const hasLikes = likes.count > 0;
  const hasComments = totalComments > 0;

  // `content_html` contains HTML tags so we use `content` to get plain text
  // unfortunately that's not enough for some items so we still need to sanitize
  const text = htmlToPlainText(content);

  return (
    <StyledLink
      key={id}
      onClick={onClick}
      onKeyDown={onKeyDown}
      tabIndex={0}
      to={href}
    >
      <ScreenReadersContent>
        {s__(
          'TopPostsAccessibility|Navigate to the following post detail page:',
        )}
      </ScreenReadersContent>
      <Card
        height={scaleWithFont(124)}
        pl={3}
        pr={4}
        py={4}
        mb={1}
        position="relative"
      >
        {/* Wrapper for top (avatar + text) and bottom (social line) */}
        <Box
          height="100%"
          width="100%"
          flexDirection="column"
          justifyContent="space-between"
        >
          <Box height="100%" width="100%">
            {/* Left column wrapper - Avatar */}
            <Box dataTestId="post-avatar">
              <UserSnapshot userId={userId}>
                <Avatar
                  alt=""
                  src={avatarUrl || avatarFallbackImage}
                  size={36}
                  mr={3}
                />
              </UserSnapshot>
            </Box>
            {/* Right column wrapper - Text + See more */}
            <Box flexDirection="column" width="100%">
              <Text clamp={2}>{text}</Text>
              <Box>
                {/* See more link */}
                <FakeLink mt={1} tabIndex={-1} aria-hidden="true">
                  {__('See more')}
                </FakeLink>
              </Box>
            </Box>
          </Box>

          {/* Social line Wrapper */}
          <Box
            alignItems="center"
            flexWrap="wrap"
            justifyContent="flex-end"
            mt={hasLikes || hasComments ? 1 : 0}
            mr={canEdit ? 7 : 0}
            font="metadata-regular-asphalt"
          >
            {hasLikes && (
              <>
                <Box mr={2} pb="1px" color="primary">
                  <IconHeart height={16} width={16} />
                </Box>
                <div>
                  {n__('%{likes} Like', '%{likes} Likes', likes.count).i__({
                    likes: likes.count.toString(),
                  })}
                </div>
              </>
            )}
            {hasLikes && hasComments && <Box mx={1}>·</Box>}
            {hasComments && (
              <span>
                {n__(
                  '%{comments} comment',
                  '%{comments} comments',
                  totalComments,
                ).i__({
                  comments: totalComments.toString(),
                })}
              </span>
            )}
          </Box>
        </Box>
        {canEdit && (
          <Box
            role="button"
            color="primary"
            tabIndex={0}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              openModal();
            }}
            onKeyDown={onEnterOrSpacePress(openModal)}
            position="absolute"
            bottom="7px"
            right="10px"
            data-testid="delete-from-top-posts"
          >
            <ScreenReadersContent>
              {s__(
                'TopPostsAccessibility|Open confirmation overlay to delete this item from Top Posts',
              )}
            </ScreenReadersContent>
            <Tooltip
              initialPosition={POSITIONS.BOTTOM}
              childrenElement={s__('TopPosts|Only visible to you as an admin')}
              hideOnScreenReader
            >
              <IconPin height={30} width={30} />
            </Tooltip>
          </Box>
        )}
      </Card>
    </StyledLink>
  );
};

export default PostCard;

const StyledLink = styled(Link)`
  text-decoration: none !important;
  & :hover {
    text-decoration: none;
  }
`;

const Text = styled(Dotdotdot).attrs(() => ({
  clamp: 3,
  font: 'body-regular-asphalt',
  fontSize: 1,
}))`
  overflow-wrap: break-word;
  word-break: break-word;
`;
